import { labChemicals, type LabChemical } from "@/data/reactions";
import { useRef, useState } from "react";
import { FlaskConical, Atom, Droplets, Wind, Search, X } from "@/lib/icons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { calculateMolarMass } from "@/data/molarMass";

interface ChemicalShelfProps {
  onAddChemical: (chemical: LabChemical) => void;
}

const categoryFilters = [
  { key: "all", label: "All" },
  { key: "element", label: "Elements" },
  { key: "compound", label: "Compounds" },
  { key: "acid", label: "Acids" },
  { key: "base", label: "Bases" },
] as const;

const stateIcons: Record<string, React.ReactNode> = {
  solid: <Atom size={10} />,
  liquid: <Droplets size={10} />,
  gas: <Wind size={10} />,
  aqueous: <FlaskConical size={10} />,
};

// Single-letter badge shown on the swatch - instant state recognition.
const stateBadge: Record<string, string> = {
  solid: "s",
  liquid: "l",
  gas: "g",
  aqueous: "aq",
};

function formatMolarMass(formula: string): string | null {
  // calculateMolarMass works with ASCII subscripts; convert.
  const ascii = formula.replace(/[₂₃₄]/g, (c) => ({ "₂": "2", "₃": "3", "₄": "4" }[c] || c));
  const result = calculateMolarMass(ascii);
  if (result.error || !result.total) return null;
  return `${result.total.toFixed(2)} g/mol`;
}

export default function ChemicalShelf({ onAddChemical }: ChemicalShelfProps) {
  const [filter, setFilter] = useState<string>("all");
  const [query, setQuery] = useState("");
  // Roving-tabindex: only one shelf button is in the tab order; arrow keys move focus.
  const [focusIdx, setFocusIdx] = useState(0);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const normalizedQuery = query.trim().toLowerCase();
  const byCategory = filter === "all" ? labChemicals : labChemicals.filter((c) => c.category === filter);
  const filtered = normalizedQuery
    ? byCategory.filter(
        (c) =>
          c.name.toLowerCase().includes(normalizedQuery) ||
          c.formula.toLowerCase().includes(normalizedQuery),
      )
    : byCategory;

  // Keep focusIdx in range when the filtered list shrinks/grows.
  const safeFocusIdx = Math.min(focusIdx, Math.max(0, filtered.length - 1));

  const handleListKeyDown = (e: React.KeyboardEvent, idx: number) => {
    let nextIdx: number | null = null;
    if (e.key === "ArrowDown") nextIdx = Math.min(filtered.length - 1, idx + 1);
    else if (e.key === "ArrowUp") nextIdx = Math.max(0, idx - 1);
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = filtered.length - 1;
    if (nextIdx !== null) {
      e.preventDefault();
      setFocusIdx(nextIdx);
      itemRefs.current[nextIdx]?.focus();
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="relative mb-2">
        <Search
          size={12}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search chemicals…"
          aria-label="Search chemicals by name or formula"
          className="w-full h-7 pl-7 pr-7 rounded-md border border-border bg-background text-[11px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground"
          >
            <X size={11} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {categoryFilters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-[11px] px-2 py-0.5 rounded-md transition-colors ${
              filter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div
        className="flex-1 overflow-y-auto space-y-1 pr-1 -mr-1"
        role="listbox"
        aria-label="Chemical shelf - use arrow keys to navigate, Enter to add"
      >
        {filtered.length === 0 && (
          <p className="text-[11px] text-muted-foreground italic px-1 py-2">
            No chemicals match "{query}".
          </p>
        )}
        {filtered.map((chem, idx) => {
          const molar = formatMolarMass(chem.formula);
          const swatchBg = chem.swatchStyle ?? chem.color;
          // Use white text on dark swatches, dark text on light. Quick HSL light extraction.
          const lightMatch = chem.color.match(/(\d+(?:\.\d+)?)%\s*\)/);
          const isLight = lightMatch ? parseFloat(lightMatch[1]) > 55 : true;
          const isFocusTarget = idx === safeFocusIdx;
          return (
            <Tooltip key={chem.id}>
              <TooltipTrigger asChild>
                <button
                  ref={(el) => { itemRefs.current[idx] = el; }}
                  role="option"
                  aria-selected={isFocusTarget}
                  tabIndex={isFocusTarget ? 0 : -1}
                  onFocus={() => setFocusIdx(idx)}
                  onKeyDown={(e) => handleListKeyDown(e, idx)}
                  onClick={() => onAddChemical(chem)}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData("chemical", JSON.stringify(chem));
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  aria-label={`Add ${chem.name} (${chem.formula}, ${chem.state})`}
                  className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-border bg-card hover:bg-accent/50 hover:border-primary/30 transition-all group text-left cursor-grab active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-primary/50"
                >
                  {/* Color swatch with state badge */}
                  <div
                    className="relative w-6 h-6 rounded-md border border-border/50 flex-shrink-0 shadow-inner overflow-hidden"
                    style={{ background: swatchBg }}
                    aria-hidden="true"
                  >
                    <span
                      className={`absolute bottom-0 right-0 px-1 leading-[10px] text-[8px] font-semibold rounded-tl-sm ${
                        isLight ? "bg-black/55 text-white" : "bg-white/80 text-black"
                      }`}
                    >
                      {stateBadge[chem.state]}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-semibold text-foreground font-mono">{chem.formula}</span>
                      <span className="text-muted-foreground">{stateIcons[chem.state]}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground truncate block">{chem.name}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    + Add
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[220px] text-xs">
                <div className="space-y-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-semibold">{chem.name}</span>
                    <span className="text-muted-foreground capitalize">{chem.state}</span>
                  </div>
                  {molar && (
                    <div className="text-muted-foreground font-mono text-[11px]">{molar}</div>
                  )}
                  {chem.uses && <div className="text-[11px]">{chem.uses}</div>}
                  {chem.safety && (
                    <div className="text-[11px] text-destructive flex gap-1 pt-0.5">
                      <span aria-hidden="true">⚠</span>
                      <span>{chem.safety}</span>
                    </div>
                  )}
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
    </TooltipProvider>
  );
}
